import type { OmniAsset } from '../store/postSlice/types'

/**
 * Collapses an additive `OmniAsset[]` (as stored on `ChannelVariant.assets`)
 * to what a non-mixed-media-capable channel's save path actually sends: one
 * media kind, chosen by precedence — link > image(s) > video > document >
 * gif. Mirrors `mapAssets`/`mapMediaAssets` (save/mapAssets.ts), which derive
 * the same precedence from a Draft's separate raw fields; this operates on
 * the already-additive array instead, for channels whose
 * `channelVariant.assets` is the save-path source.
 *
 * A link-kind entry is always alone when present — `draftMediaToAssets`
 * (mapDraftToOmni.ts) guarantees a link that wins the attachment gate
 * produces exactly `[{ kind: 'link', ... }]`, nothing else — so checking for
 * it first is sufficient, not a filter over the rest of the array.
 *
 * Only the first video-kind entry is kept, matching `mapMediaAssets`'s single
 * `source.video` field: a non-mixed-media-capable channel's `canHaveMultipleVideos`
 * is false, so its UI never attaches more than one, but this mirrors legacy
 * exactly rather than assuming that invariant holds.
 */
export function selectAssetsByPrecedence(assets: OmniAsset[]): OmniAsset[] {
  const link = assets.find((asset) => asset.kind === 'link')
  if (link) return [link]

  const images = assets.filter((asset) => asset.kind === 'image')
  if (images.length > 0) return images

  const video = assets.find((asset) => asset.kind === 'video')
  if (video) return [video]

  const document = assets.find((asset) => asset.kind === 'document')
  if (document) return [document]

  const gif = assets.find((asset) => asset.kind === 'gif')
  if (gif) return [gif]

  return []
}
